import type { ColumnsType } from 'antd/es/table';
import { type Key, useCallback, useMemo, useRef } from 'react';

import { ROW_HIGHLIGHT_CLASS } from '../../constants';
import type { CellHighlight, HighlightTone, RowHighlight } from '../../types';
import getHighlightColumnKey from '../../utils/getHighlightColumnKey';

type Props = {
  rowHighlights?: RowHighlight[];
  cellHighlights?: CellHighlight[];
  rowKey?: PropertyKey | ((record: unknown, index?: number) => Key);
};

type IndexedRowHighlight = {
  tone: HighlightTone;
  marker: boolean;
};

const TONE_PRECEDENCE: Record<HighlightTone, number> = {
  info: 1,
  success: 2,
  warning: 3,
  danger: 4
};

const getRowKey = (
  record: unknown,
  rowKey?: PropertyKey | ((record: unknown, index?: number) => Key),
  index?: number
): Key => {
  if (typeof rowKey === 'function') return rowKey(record, index);
  if (rowKey !== undefined) {
    return (record as Record<PropertyKey, Key>)?.[rowKey] ?? index ?? 'key';
  }
  return (record as Record<string, Key>)?.key ?? index ?? 'key';
};

const useTableHighlights = ({
  rowHighlights,
  cellHighlights,
  rowKey
}: Props) => {
  const rowHighlightMap = useMemo(() => {
    const highlights = new Map<Key, IndexedRowHighlight>();

    for (const highlight of rowHighlights ?? []) {
      const current = highlights.get(highlight.key);
      const tone =
        current &&
        TONE_PRECEDENCE[current.tone] > TONE_PRECEDENCE[highlight.tone]
          ? current.tone
          : highlight.tone;

      highlights.set(highlight.key, {
        tone,
        marker: Boolean(current?.marker || highlight.marker)
      });
    }

    return highlights;
  }, [rowHighlights]);

  const cellHighlightMap = useMemo(() => {
    const highlights = new Map<Key, Map<string, HighlightTone>>();

    for (const highlight of cellHighlights ?? []) {
      const rowHighlights =
        highlights.get(highlight.rowKey) ?? new Map<string, HighlightTone>();
      const currentTone = rowHighlights.get(highlight.columnKey);

      if (
        !currentTone ||
        TONE_PRECEDENCE[highlight.tone] >= TONE_PRECEDENCE[currentTone]
      ) {
        rowHighlights.set(highlight.columnKey, highlight.tone);
      }

      highlights.set(highlight.rowKey, rowHighlights);
    }

    return highlights;
  }, [cellHighlights]);

  const rowHighlightMapRef = useRef(rowHighlightMap);
  rowHighlightMapRef.current = rowHighlightMap;

  const cellHighlightMapRef = useRef(cellHighlightMap);
  cellHighlightMapRef.current = cellHighlightMap;

  const getRowHighlightClassName = useCallback(
    (record: unknown, index?: number) => {
      const highlight = rowHighlightMapRef.current.get(
        getRowKey(record, rowKey, index)
      );
      if (!highlight) return '';

      return [
        `${ROW_HIGHLIGHT_CLASS}--${highlight.tone}`,
        highlight.marker && `${ROW_HIGHLIGHT_CLASS}--marker`
      ]
        .filter(Boolean)
        .join(' ');
    },
    [rowKey]
  );

  const getCellHighlightClassName = useCallback(
    (record: unknown, columnKey: string, index?: number) => {
      const tone = cellHighlightMapRef.current
        .get(getRowKey(record, rowKey, index))
        ?.get(columnKey);

      return tone ? `table-cell-highlight--${tone}` : '';
    },
    [rowKey]
  );

  const getCellHighlightedColumns = useCallback(
    <T extends object>(columns?: ColumnsType<T>) => {
      if (!cellHighlights?.length) return columns;

      return columns?.map(column => {
        const columnKey = getHighlightColumnKey(column);
        if (!columnKey) return column;

        return {
          ...column,
          onCell: (record: T, index?: number) => {
            const cellProps = column.onCell?.(record, index) ?? {};
            const highlightClassName = getCellHighlightClassName(
              record,
              columnKey,
              index
            );

            if (!highlightClassName) return cellProps;

            return {
              ...cellProps,
              className: [cellProps.className, highlightClassName]
                .filter(Boolean)
                .join(' ')
            };
          }
        };
      });
    },
    [cellHighlights?.length, getCellHighlightClassName]
  );

  return {
    getRowHighlightClassName,
    getCellHighlightClassName,
    getCellHighlightedColumns
  };
};

export default useTableHighlights;
